import {
  AddCircleOutline,
  ExpandMore,
  HighlightOff,
  Info,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { addSelectedVariant } from "../../../redux/Catalog/catalog";
import SelectVariants from "./selectVariants";
import CustomVariant from "./customVariant";

const formulationTypeOptions = [
  { type: "Powder", units: ["g", "kg", "mg"] },
  { type: "Liquid", units: ["ml", "l"] },
  { type: "Granule", units: ["mg", "g", "kg"] },
  { type: "Pellet", units: ["count"] },
  { type: "Gel", units: ["g", "ml", "kg", "l"] },
  { type: "Tablet", units: ["count"] },
  { type: "Suspension", units: ["ml", "l"] },
  { type: "Emulsion", units: ["ml", "l"] },
  { type: "Solution", units: ["ml", "l"] },
];

const Variant = ({
  selected,
  setSelected,
  setVariants,
  variants,
  children,
  initialFormulations = null,
  initialVariantEnabled = false,
}) => {
  const [variant, setVariant] = useState(initialVariantEnabled);
  const [isOpenVariant, setIsOpenVariant] = useState(null);
  const [isOpenUnit, setIsOpenUnit] = useState(null);
  const dispatch = useDispatch();
  const [isVariantType, setIsVariantType] = useState(false);
  const [formulations, setFormulations] = useState(
    initialFormulations || [
      {
        type: "",
        units: [],
        values: [
          {
            value: "",
            unit: "",
          },
        ],
      },
    ]
  );

  const handleValueChange = (formulationIdx, valueIdx, value) => {
    setFormulations((prev) =>
      prev.map((f, i) =>
        i === formulationIdx
          ? {
              ...f,
              values: f.values.map((v, vi) =>
                vi === valueIdx ? { ...v, value } : v
              ),
            }
          : f
      )
    );
  };

  const handleUnitSelect = (formulationIdx, valueIdx, selectedUnit) => {
    setFormulations((prev) =>
      prev.map((f, i) =>
        i === formulationIdx
          ? {
              ...f,
              values: f.values.map((v, vi) =>
                vi === valueIdx ? { ...v, unit: selectedUnit } : v
              ),
            }
          : f
      )
    );
    setIsOpenUnit(null);
  };

  const handleAddMoreValue = (formulationIdx) => {
    setFormulations((prev) =>
      prev.map((f, i) =>
        i === formulationIdx
          ? {
              ...f,
              values: [...f.values, { value: "", unit: "" }],
            }
          : f
      )
    );
  };

  const handleRemoveValue = (formulationIdx, valueIdx) => {
    setFormulations((prev) =>
      prev.map((f, i) =>
        i === formulationIdx
          ? {
              ...f,
              values: f.values.filter((_, vi) => vi !== valueIdx),
            }
          : f
      )
    );
  };

  const handleAddMore = () => {
    setFormulations([
      ...formulations,
      {
        type: "",
        units: [],
        values: [
          {
            value: "",
            unit: "",
          },
        ],
      },
    ]);
    setIsOpenVariant(formulations.length);
  };

  const handleFormulationTypeSelect = (index, selectedTypeObj) => {
    const updatedFormulations = formulations.map((f, i) =>
      i === index
        ? {
            ...f,
            type: selectedTypeObj.type,
            units: selectedTypeObj.units,
            values: [
              {
                value: "",
                unit: "",
              },
            ],
          }
        : f
    );
    setFormulations(updatedFormulations);
    setIsOpenVariant(null);
  };

  const handleCustomVariants = (type) => {
    if (type === "color") {
      setVariants((prev) => ({
        ...prev,
        colour: {
          ...prev.colour,
          custom: true,
        },
      }));
    }
    if (type === "custom") {
      const newKey = `custom_${Date.now()}`;
      setVariants((prev) => ({
        ...prev,
        [newKey]: {
          custom: true,
          label: "Custom Variant",
          values: [""],
        },
      }));
    }
  };

  const handleClickVariant = (type) => {
    if (type === "color") {
      handleCustomVariants("color");
      setIsVariantType(false);
    } else if (type === "custom") {
      handleCustomVariants("custom");
      setIsVariantType(false);
    }
  };
  useEffect(() => {
    dispatch(addSelectedVariant(selected));
  }, [selected, dispatch]);

  const allFormulationsHaveSizes =
    formulations.filter((f) => f.type).length > 0 &&
    formulations
      .filter((f) => f.type)
      .every(
        (f) => f.values.length > 0 && f.values.every((v) => v.value && v.unit)
      );

  const colorSelected = !!(
    typeof variants === "object" && variants?.colour?.custom
  );

  // Custom Variant Handlers
  const handleAddCustomVariant = () => {
    const newKey = `custom_${Date.now()}`;
    setVariants((prev) => ({
      ...prev,
      [newKey]: {
        custom: true,
        label: "",
        values: [""],
      },
    }));
    setIsVariantType(false);
  };

  return (
    <div>
      <div className="rounded-lg bg-white h-fit border border-[#E1E6E1] mt-3">
        {/* VARIANT TOGGLE */}
        <div className="flex py-3 border-b justify-between px-3 items-center border-b-[#E1E6E1] gap-[10px]">
          <div className="flex flex-col gap-1">
            <p className="text-[#2E332F] font-semibold text-sm leading-[18px]">
              Variants
            </p>
            <p className="text-[#96A397] font-medium text-sm leading-[18px]">
              This product has multiple variants
            </p>
          </div>
          <div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={variant}
                value={variant}
                onChange={
                  formulations.length > 1
                    ? () => {
                        toast.info(
                          "You can't turn this off while multiple variants are selected"
                        );
                      }
                    : () => setVariant(!variant)
                }
                className="sr-only peer"
              />
              <div className="w-[48px] h-[24px] bg-[#96A397] peer-focus:outline-none rounded-full peer peer-checked:bg-[#2B8C34] transition-all duration-200"></div>
              <div className="absolute left-[3px] top-0.7 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-[24px] transition-transform duration-200"></div>
            </label>
          </div>
        </div>
        {/* FORMULATION TYPE */}
        <div className="m-3 bg-[#F6F7F6] border rounded-lg border-[#E1E6E1]">
          <div className="rounded-lg bg-white py-3 h-fit border border-[#E1E6E1] m-3">
            <div className="flex justify-between items-center gap-[10px] px-3">
              <div className="flex flex-col gap-1">
                <p className="text-[#2E332F] font-semibold text-sm leading-[18px]">
                  Formulation Type
                </p>
                <div className="flex gap-1 items-center">
                  <Info style={{ color: "#0066AF", fontSize: "16px" }} />
                  <p className="text-[#96A397] font-medium text-sm leading-[18px]">
                    Powder, Liquid, Granule
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-[#E1E6E1] my-2 h-[1.2px] w-full" />
            {/* VARIANT OPTIONS */}

            <div className="m-3 bg-white border rounded-lg border-[#E1E6E1]">
              <div className="border-b border-b-[#E1E6E1]">
                <p className="text-[#3E473F] font-semibold text-sm leading-[18px] p-3">
                  Variant options
                </p>
              </div>
              <div className="px-3">
                <div>
                  {formulations.map((formulation, index) => (
                    <div key={index} className="relative w-full py-3">
                      {/* Select Formulation Type */}
                      <div
                        className="flex justify-between items-center px-[16px] border border-[#E1E6E1] rounded-[8px] h-[44px] w-full cursor-pointer bg-white"
                        onClick={() =>
                          setIsOpenVariant(
                            isOpenVariant === index ? null : index
                          )
                        }
                        style={{
                          border:
                            isOpenVariant === index ? "1px solid #2B8C34" : "",
                        }}
                      >
                        <p className="font-semibold text-sm leading-[20px] text-[#2E332F]">
                          {formulation.type || "Select formulation type"}
                        </p>
                        <ExpandMore
                          className="text-[#ABB6AC] cursor-pointer"
                          style={{ fontSize: "18px" }}
                        />
                      </div>
                      {/* Dropdown */}
                      {isOpenVariant === index && (
                        <div className="absolute left-0 w-full bg-white py-[8px] cursor-pointer mt-2 z-20 max-h-[250px] overflow-y-scroll shadow-md border rounded-lg">
                          {formulationTypeOptions
                            .filter(
                              (data) =>
                                !formulations.some(
                                  (f, i) => f.type === data.type && i !== index
                                )
                            )
                            .map((data, idx) => (
                              <p
                                className="py-[12px] px-[16px] text-sm font-medium leading-5 hover:bg-[#EDF7EE]"
                                key={idx}
                                onClick={() =>
                                  handleFormulationTypeSelect(index, data)
                                }
                              >
                                {data.type}
                              </p>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {variant && (
                <div
                  className="border-t border-t-[#E1E6E1] flex items-center gap-1 p-3 cursor-pointer hover:bg-[#EDF7EE] group"
                  onClick={handleAddMore}
                >
                  <AddCircleOutline
                    style={{ fontSize: "20px", color: "#2B8C34" }}
                  />
                  <p className="text-[#2B8C34] font-semibold text-sm leading-[18px]">
                    Add More
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* SIZE */}
          {formulations[0].type.length !== 0 && (
            <div className="rounded-lg bg-white py-3 h-fit border border-[#E1E6E1] m-3">
              <div className="flex justify-between items-center gap-[10px] px-3">
                <div className="flex flex-col gap-1">
                  <p className="text-[#2E332F] font-semibold text-sm leading-[18px]">
                    Size
                  </p>
                  <div className="flex gap-1 items-center">
                    <Info style={{ color: "#0066AF", fontSize: "16px" }} />
                    <p className="text-[#96A397] font-medium text-sm leading-[18px]">
                      Weight, Volume, Count
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#E1E6E1] my-2 h-[1.2px] w-full" />
              {formulations?.map((size, formulationIdx) => {
                return (
                  <div
                    key={formulationIdx}
                    className="mx-3 my-6 bg-white border rounded-lg border-[#E1E6E1]"
                  >
                    <div className="border-b border-b-[#E1E6E1] flex justify-between items-center p-3">
                      <p className="text-[#3E473F] font-semibold text-sm leading-[18px] ">
                        {size?.type}
                      </p>
                      {formulationIdx !== 0 && (
                        <HighlightOff
                          onClick={() => {
                            setFormulations(
                              formulations.filter(
                                (_, idx) => idx !== formulationIdx
                              )
                            );
                          }}
                          style={{
                            color: "#96A397",
                            fontSize: "20px",
                            cursor: "pointer",
                          }}
                        />
                      )}
                    </div>
                    <div className="">
                      <div>
                        {size?.values?.map((formulation, valueIdx) => (
                          <div key={valueIdx}>
                            <div className="flex gap-3 items-center px-3 py-3">
                              {/* Value Input */}
                              <input
                                type="text"
                                className="border border-[#E1E6E1] rounded-[8px] h-[44px] px-3 w-[85%]  text-sm outline-none focus:border-[#2B8C34] "
                                placeholder={`Enter size`}
                                value={formulation.value}
                                onChange={(e) =>
                                  handleValueChange(
                                    formulationIdx,
                                    valueIdx,
                                    e.target.value
                                  )
                                }
                                disabled={!size.type}
                              />
                              {/* Unit Dropdown */}
                              <div className="relative w-[15%] ">
                                <div
                                  className={`flex justify-between items-center px-2 border border-[#E1E6E1] rounded-[8px] h-[44px] w-full cursor-pointer bg-white ${
                                    !size.type
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    size.type &&
                                    setIsOpenUnit(
                                      isOpenUnit ===
                                        `${formulationIdx}-${valueIdx}`
                                        ? null
                                        : `${formulationIdx}-${valueIdx}`
                                    )
                                  }
                                  style={{
                                    border:
                                      isOpenUnit ===
                                      `${formulationIdx}-${valueIdx}`
                                        ? "1px solid #2B8C34"
                                        : "",
                                  }}
                                >
                                  <p className="font-semibold text-sm leading-[20px] text-[#2E332F]">
                                    {formulation.unit || "unit"}
                                  </p>
                                  <ExpandMore
                                    className="text-[#ABB6AC] cursor-pointer"
                                    style={{ fontSize: "18px" }}
                                  />
                                </div>
                                {/* Unit Dropdown */}
                                {isOpenUnit ===
                                  `${formulationIdx}-${valueIdx}` &&
                                  size.units.length > 0 && (
                                    <div className="absolute left-0 w-full bg-white py-[8px] cursor-pointer mt-2 z-20 max-h-[250px] overflow-y-scroll shadow-md border rounded-lg">
                                      {size.units.map((data, idx) => (
                                        <p
                                          className="py-[12px] px-[16px] text-sm font-medium leading-5 hover:bg-[#EDF7EE]"
                                          key={idx}
                                          onClick={() =>
                                            handleUnitSelect(
                                              formulationIdx,
                                              valueIdx,
                                              data
                                            )
                                          }
                                        >
                                          {data}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                              </div>
                              {/* Show remove icon only if not the first value */}
                              {valueIdx > 0 && (
                                <HighlightOff
                                  style={{
                                    color: "#96A397",
                                    fontSize: "20px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    handleRemoveValue(formulationIdx, valueIdx)
                                  }
                                />
                              )}
                            </div>
                            {/* Add more formulation types */}
                            {variant && valueIdx === size.values.length - 1 && (
                              <div
                                className="border-t border-t-[#E1E6E1] flex items-center gap-1 p-3 cursor-pointer hover:bg-[#EDF7EE] group"
                                onClick={() =>
                                  handleAddMoreValue(formulationIdx)
                                }
                                style={{
                                  opacity:
                                    !formulation.value || !formulation.unit
                                      ? 0.5
                                      : 1,
                                  pointerEvents:
                                    !formulation.value || !formulation.unit
                                      ? "none"
                                      : "auto",
                                }}
                              >
                                <AddCircleOutline
                                  style={{ fontSize: "20px", color: "#2B8C34" }}
                                />
                                <p className="text-[#2B8C34] font-semibold text-sm leading-[18px]">
                                  Add More
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* COLOUR */}
          <div>{children}</div>
        </div>

        {/* CUSTOM VARIANTS */}
        {Object.keys(variants)
          .filter((key) => key !== "colour" && variants[key]?.custom)
          .map((key) => (
            <CustomVariant
              key={key}
              variants={variants}
              setVariants={setVariants}
              variantKey={key}
            />
          ))}

        {/* ADD VARIANT TYPES */}
        <div className="relative">
          <div
            onClick={() => {
              if (allFormulationsHaveSizes && variant)
                setIsVariantType(!isVariantType);
            }}
            className={`border-t border-t-[#E1E6E1] flex items-center gap-1 p-3 group ${
              !allFormulationsHaveSizes || !variant
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer hover:bg-[#EDF7EE]"
            }`}
            aria-disabled={!allFormulationsHaveSizes || !variant}
          >
            <AddCircleOutline style={{ fontSize: "20px", color: "#2B8C34" }} />
            <p className="text-[#2B8C34] font-semibold text-sm leading-[18px]">
              Add Variant Type
            </p>
          </div>

          {isVariantType && (
            <div className="absolute z-3 top-[-60px] ">
              <div
                className="fixed inset-0 z-0"
                style={{ top: 0, left: 0, width: "100vw", height: "100vh" }}
                onClick={() => setIsVariantType(false)}
              />
              <div className="bg-white shadow-sm shadow-[#00000014] w-[348px] h-fit p-2 rounded-lg cursor-pointer relative z-10">
                <div
                  className={`py-3 px-4 w-full ${
                    colorSelected ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  onClick={() => handleClickVariant("color")}
                  aria-disabled={colorSelected}
                >
                  <p className="text-[#344335] font-medium text-sm ">Colour</p>
                </div>

                <div
                  onClick={handleAddCustomVariant}
                  className="py-3 px-4 w-full border-t border-t-[#E1E6E1] flex gap-2 items-center"
                >
                  <AddCircleOutline
                    style={{ color: "#3E473F", fontSize: "20px" }}
                  />
                  <p className="text-[#3E473F] font-semibold text-sm ">
                    Add custom variant type
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* LIST VARIANTS TO SELECT FROM */}
      <SelectVariants
        formulations={formulations}
        selected={selected}
        variants={variants}
        setVariants={setVariants}
        setSelected={setSelected}
      />
    </div>
  );
};

export default Variant;
